import React, { useEffect, useState } from "react";
import "./AvailableHomes.scss";
import { useAnalytics } from "../../customHooks/reactGA";

import Appbar from "../../components/Appbar/Appbar";
import Footer from "../../components/Footer/Footer";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  CardMedia,
  Grid
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import iconLight from '../../assets/carp_house_plain.png';
import iconDark from '../../assets/carp-house-plain-dark.png';
import Seo from "../../components/Seo/Seo";

const LISTINGS_URL =
  "https://opensheet.elk.sh/1SsoCLxinaVJC4JOEb0VmlmQltqpMG3Ks1Tgk_VwBgZ4/Current%20Listings%20-%20CarpHomes";

const AvailableHomes = (props) => {
  const { trackPageView, trackEvent } = useAnalytics();

  useEffect(() => {
    trackPageView("/available-homes");
  }, [trackPageView]);

  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [listingsError, setListingsError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadListings = async () => {
      setLoadingListings(true);
      setListingsError(false);

      try {
        const res = await fetch(LISTINGS_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`Listings fetch failed: ${res.status}`);

        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];

        // Normalize sheet rows into the shape your UI expects.
        const normalized = rows
          .filter((r) => r && (r.id || r.address)) // allow address-only rows too
          .map((r) => {
            const id = (r.id || r.address || "")
              .toString()
              .trim()
              .toLowerCase()
              .replace(/\s+/g, "-");

            const title = (r.address || "").toString().trim();
            const cityStateZip = (r.cityStateZip || "").toString().trim();
            const imageUrl = (r.imageUrl || "").toString().trim();

            const homesUrl = (r.homesUrl || "").toString().trim();
            const zillowUrl = (r.zillowUrl || "").toString().trim();

            // Build buttons from whatever links exist.
            const links = [
              homesUrl
                ? { label: "View on Homes.com", href: homesUrl }
                : null,
              zillowUrl
                ? { label: "View on Zillow", href: zillowUrl }
                : null,
            ].filter(Boolean);

            return {
              id,
              title,
              cityStateZip,
              imageUrl,
              notes: links.length ? "View listing on Homes.com or Zillow." : "",
              links,
            };
          })
          // If someone leaves a blank row in the sheet, this keeps things tidy:
          .filter((h) => h.title || h.links.length);

        if (isMounted) setListings(normalized);
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setListings([]);
          setListingsError(true);
        }
      } finally {
        if (isMounted) setLoadingListings(false);
      }
    };

    loadListings();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasListings = listings.length > 0;

  return (
    <div>
      <Seo
        title="Available Homes"
        description="Browse the Indianapolis-area homes CarpHomes has recently purchased."
        pathname="/available-homes"
        />
      <Appbar colorMode={props.colorMode} theme={props.theme} />

      <section
        className="landing"
        style={{
          backgroundColor:
            props.theme.palette.mode === "dark"
              ? props.theme.palette.primary.dark
              : '#e4e4e4',
          backgroundImage:
            props.theme.palette.mode === "dark"
              ? `url(${iconDark})`
              : `url(${iconLight})`
        }}
      >
        <div className="flex-container main-text-container">
          <div>
            <h1
              className="heading-title l1 heading-title--pop"
              style={{
                color:
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.warning.main
                    : props.theme.palette.primary.main,
              }}
            >
              Available Homes
            </h1>
            <p
              className="heading-title l2"
              style={{
                color:
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.common.white
                    : props.theme.palette.primary.dark,
              }}
            >
              If you have questions or want to schedule a showing, please reach out via our contact form.</p>
          </div>
          <Button
            variant="contained"
            sx={{
              backgroundColor:
                props.theme.palette.mode === "dark"
                  ? props.theme.palette.warning.main
                  : props.theme.palette.primary.main,
            }}
            component={RouterLink}
            to="/contact"
          >
            <Typography color={props.theme.palette.common.white}>
              Get in Touch
            </Typography>
          </Button>
        </div>
      </section>

      {/* HERO / EMPTY STATE (only when not loading and no listings) */}
      {!loadingListings && !hasListings && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            height: "calc(100vh - 64px)",
            backgroundColor: "#e4e4e4",
          }}
        >
          <Box mb={5}>
            {props.theme.palette.mode === "dark" ? (
              <img
                src={iconLight}
                height="150px"
                width="auto"
                alt="CarpHomes House Logo light"
              />
            ) : (
              <img
                src={iconDark}
                height="150px"
                width="auto"
                alt="CarpHomes House Logo dark"
              />
            )}
          </Box>

          <Box textAlign="center">
            <Typography
              sx={{
                fontFamily: "karla",
                fontSize: "1.5rem",
                color:
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.secondary.light
                    : props.theme.palette.primary.dark,
              }}
            >
              {listingsError
                ? "We couldn’t load listings right now."
                : "We currently do not have any available listings."}
            </Typography>
            <br />
            <Typography
              sx={{
                color:
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.common.white
                    : props.theme.palette.primary.main,
              }}
            >
              {listingsError
                ? "Please try again in a bit."
                : "Please check back again soon."}
            </Typography>
          </Box>
        </Box>
      )}

      <div className="content-wrapper">
        {/* LISTINGS (only when listings exist) */}
        {hasListings && (
          <Box mb={10} mt={15}>
            {/* <Typography
              variant="h4"
              fontFamily={"karla"}
              mb={2.5}
              color={
                props.theme.palette.mode === "dark"
                  ? props.theme.palette.secondary.light
                  : props.theme.palette.primary.dark
              }
            >
              Available Homes
            </Typography>

            <Typography
              mb={3}
              color={
                props.theme.palette.mode === "dark"
                  ? props.theme.palette.common.white
                  : props.theme.palette.primary.dark
              }
            >
              Here are our current listings. If you have questions or want to
              schedule a showing, reach out anytime.
            </Typography> */}

            <Grid container spacing={2}>
              {listings.map((home) => (
                <Grid item key={home.id} xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor:
                        props.theme.palette.mode === "dark"
                          ? props.theme.palette.primary.light
                          : props.theme.palette.common.white,
                    }}
                  >
                    {/* Card image */}
                    {home.imageUrl ? (
                      <CardMedia
                        component="img"
                        height="220"
                        image={home.imageUrl}
                        alt={home.title || "Home listing"}
                        sx={{ objectFit: "cover" }}
                        loading="lazy"
                      />
                    ) : null}

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        fontFamily={"karla"}
                        color={
                          props.theme.palette.mode === "dark"
                            ? props.theme.palette.secondary.light
                            : props.theme.palette.primary.dark
                        }
                      >
                        {home.title}
                      </Typography>

                      {home.cityStateZip ? (
                        <Typography
                          mt={0.5}
                          color={
                            props.theme.palette.mode === "dark"
                              ? props.theme.palette.common.white
                              : props.theme.palette.primary.main
                          }
                        >
                          {home.cityStateZip}
                        </Typography>
                      ) : null}

                      {home.notes ? (
                        <Typography
                          mt={2}
                          color={
                            props.theme.palette.mode === "dark"
                              ? props.theme.palette.common.white
                              : props.theme.palette.primary.dark
                          }
                        >
                          {home.notes}
                        </Typography>
                      ) : null}

                      {home.links.length ? (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="body2"
                            color={
                              props.theme.palette.mode === "dark"
                                ? props.theme.palette.common.white
                                : props.theme.palette.primary.dark
                            }
                          >
                            View listing:
                          </Typography>
                        </>
                      ) : null}
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 2 }}>
                      <Grid container spacing={1}>
                        {home.links.map((l) => (
                          <Grid item key={l.href}>
                            <Button
                              variant="contained"
                              color="warning"
                              component="a"
                              href={l.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                            >
                              {l.label}
                            </Button>
                          </Grid>
                        ))}

                        <Grid item>
                          <Button
                            variant="outlined"
                            component={RouterLink}
                            to="/contact"
                            size="small"
                            sx={{
                              borderColor:
                                props.theme.palette.mode === "dark"
                                  ? props.theme.palette.secondary.light
                                  : props.theme.palette.primary.main,
                              color:
                                props.theme.palette.mode === "dark"
                                  ? props.theme.palette.secondary.light
                                  : props.theme.palette.primary.main,
                            }}
                          >
                            Ask about this home
                          </Button>
                        </Grid>
                      </Grid>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>


          </Box>
        )}

        {/* Optional: show a lightweight loading message while fetching */}
        {loadingListings && (
          <Box mt={10} mb={10}>
            <Typography
              color={
                props.theme.palette.mode === "dark"
                  ? props.theme.palette.common.white
                  : props.theme.palette.primary.dark
              }
            >
              Loading listings...
            </Typography>
          </Box>
        )}

        {/* Your existing content continues below */}
        
        <section className="contact-form">
          {/* <Box
            mt={15}
            mb={10}
          >
            <Typography
              sx={{
                color:
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.common.white
                    : props.theme.palette.primary.main,
                fontSize: "2rem",
                fontFamily: "karla",
                marginBottom: '0.5em',
                textAlign: 'center'
              }}
            >
              Get Your Free, No-Obligation Cash Offer Today
            </Typography>
            <Form theme={props.theme} />
          </Box> */}
          <Box
            mt={15}
            bgcolor={
              props.theme.palette.mode === "dark"
                ? props.theme.palette.primary.light
                : props.theme.palette.secondary.main
            }
            sx={{
              borderRadius: "25px 25px 0 0",
            }}
          >
            <Box className="contact-us-text-container">
              <Typography
                variant="h4"
                fontSize={'2.25rem'}
                fontFamily={"karla"}
                mb={2.5}
                mt={5}
                color={
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.secondary.light
                    : props.theme.palette.primary.dark
                }
              >
                Supplemental Services
              </Typography>
              <Typography
                lineHeight={2.5}
                color={
                  props.theme.palette.mode === "dark"
                    ? props.theme.palette.common.white
                    : props.theme.palette.primary.dark
                }
              >
                In addition to our core focus of purchasing properties directly, our team offers supplemental services tailored to specific needs:
              </Typography>
              <Box>
                <ul>
                  <li>
                    <Typography
                      lineHeight={2.5}
                      color={
                        props.theme.palette.mode === "dark"
                          ? props.theme.palette.common.white
                          : props.theme.palette.primary.dark
                      }
                    >
                      <span>
                        <b>Realtor Services - </b>
                      </span>
                      If you're considering selling your home through the traditional market, our team can assist with listing your property and navigating the sales process. While our primary focus is on purchasing homes, this service is available for homeowners who prefer a traditional approach.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      lineHeight={2.5}
                      color={
                        props.theme.palette.mode === "dark"
                          ? props.theme.palette.common.white
                          : props.theme.palette.primary.dark
                      }
                    >
                      <span>
                        <b>Construction Services for Investors - </b>
                      </span>
                      We provide expert construction management services exclusively for real estate investors. Whether you're a seasoned investor or new to the market, we can oversee renovation projects to maximize value and streamline the process. This service is ideal for out-of-state investors looking to expand their portfolio in the Indianapolis area without the hassle of managing projects locally.                </Typography>
                  </li>
                </ul>
              </Box>

              <Box mt={10}>
                <Typography
                  variant="h4"
                  fontSize={'2.25rem'}
                  fontFamily={"karla"}
                  color={
                    props.theme.palette.mode === "dark"
                      ? props.theme.palette.warning.main
                      : props.theme.palette.primary.main
                  }
                mb={2.5}
                // textAlign={'center'}
                >
                  Sell Your Indiana Home Fast with CarpHomes
                </Typography>
                <Typography
                  lineHeight={2.5}
                  mt={2}
                  color={
                    props.theme.palette.mode === "dark"
                      ? props.theme.palette.common.white
                      : props.theme.palette.primary.dark
                  }
                // textAlign={'center'}
                >
                  We make selling your home easy, fast, and stress-free. As a local Indiana business, we offer cash purchases with no realtor commissions or closing costs. Close in as little as 7 days—no repairs, no open houses, no hassle. Enjoy a private, personalized experience with a team that understands your needs every step of the way.
                </Typography>
              </Box>

              <Box display="flex" justifyContent="center" mt={5}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor:
                      props.theme.palette.mode === "dark"
                        ? props.theme.palette.warning.main
                        : props.theme.palette.primary.main,
                  }}
                  onClick={() => trackEvent('CTA', 'Click', 'Reach Out')}
                  component={RouterLink}
                  to="/contact"
                >
                    <Typography color={props.theme.palette.common.white}>
                      Reach Out
                    </Typography>
                </Button>
              </Box>

            </Box>
          </Box>

        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AvailableHomes;
